import React, { useState } from 'react';
import ModalCreateFuelTank from './ModalCreateFuelTank';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import FuelTank from './FuelTank';
import client from '../../../client';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';

const FuelInventory = () => {
  const { t } = useTranslation('navigation');
  const [fuelBatches, setFuelBatches] = React.useState([]);
  const [fuelTanks, setFuelTanks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [totalGallonsDispensed, setTotalGallonsDispensed] = React.useState(0);
  const [totalFuelCost, setTotalFuelCost] = React.useState(0);
  const [totalTransactions, setTotalTransactions] = React.useState(0);
  const [totalFuelPurchases, setTotalFuelPurchases] = React.useState(0);
  const [fuelnLubeCatalog, setFuelnLubeCatalog] = React.useState([]);
  const fuelInventoryValue = getFuelInventoryValue(fuelBatches);

    // Get compacctnum from localStorage
  let compacctnum = '';
  try {
    const lSData = JSON.parse(localStorage.getItem('persist:root'));
    const userData = JSON.parse(lSData.loadz_auth).AuthReducer.user;
    compacctnum = userData.company.compacctnum;
  } catch (e) {
    compacctnum = '';
  }

  const getFullData = async () => {
    setLoading(true);
    try {
      const response = await client.get(`/fuel-inventory/get-dashboard-full-data/${compacctnum}`);
      if (response && response.data) {
        setFuelTanks(response.data.data.fuelTankData || []);
        setFuelBatches(response.data.data.fuelBatchData || []);
        setTotalGallonsDispensed(response.data.data.totalGallonsDispensed || 0);
        setTotalFuelCost(response.data.data.totalFuelCost || 0);
        setTotalTransactions(response.data.data.totalTransactions || 0);
        setTotalFuelPurchases(response.data.data.totalFuelPurchases || 0);
        setFuelnLubeCatalog(response.data.data.fuelNLubeCatalogData || []);
      }
    } catch (e) {
      setFuelTanks([]);
    }
    setLoading(false);
  };

  React.useEffect(() => {
    getFullData();
  }, []);

  // Calculate total gallons
  const totalGallons = fuelBatches.reduce((sum, batch) => sum + Math.fround(batch.fuel_qty), 0);

  // Maximum tank capacity (you can adjust this value)
  const tankCapacity = 2000;

  // Calculate fill percentage
  const fillPercent = Math.min(totalGallons / tankCapacity, 1);

  // Calculate the height of each batch in the tank
  const batchHeights = fuelBatches.map(batch => ({
    ...batch,
    percent: batch.gallons / tankCapacity,
  }));

  const [openCreateTank, setOpenCreateTank] = useState(false);

  return (
    <div className="p-24 justify-start">
      <ModalCreateFuelTank isOpen={openCreateTank} setIsOpen={setOpenCreateTank} onCreated={getFullData} _fuelnLubeCatalog={fuelnLubeCatalog} />
      <div className="flex-row gap-12">
        <div>
          <div className="flex flex-1 justify-between items-center mb-4">
            {/*header title */}
            <h1 className="text-2xl font-bold">{t('FUEL_AND_LUBE_DASHBOARD')}</h1>
            {/* Button to create tank */}
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateTank(true)}
            >
              {t('ADD_TANK')}
            </Button>
          </div>
          {/*Fuel Inventory*/}
          <div className="flex-1">
              {/* Render multiple tanks if they exist in fuelTanks */}
              {loading ? (
                <div className="flex flex-col items-center justify-center min-w-[220px]">
                  <b style={{ fontSize: '2rem', fontStyle: 'italic', color: '#888' }}>{t('LOADING')}</b>
                </div>
              ) : fuelTanks.length > 0 ? (
                <div style={{ width: '100% !important' }} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 lg:grid-cols-6 xl:grid-cols-6 gap-4 sm:gap-6 md:gap-8">
                  {fuelTanks
                    .sort((a, b) => {
                      // Sort tanks: first those that have content (batches)
                      const aHasContent = getBatches(a.id).length > 0;
                      const bHasContent = getBatches(b.id).length > 0;
                      
                      if (aHasContent && !bHasContent) return -1; // a goes first
                      if (!aHasContent && bHasContent) return 1;  // b goes first
                      return 0; // maintain original order if both have or don't have content
                    })
                    .map((tank, idx) => (
                    <FuelTank
                      key={tank.id || idx}
                      tankCapacity={tank.capacity || tank.tankCapacity || 2000}
                      tankKindOfContent={tank.product_name || ''}
                      tankLocation={tank.location || ''}
                      tankNotes={tank.notes || ''}
                      batchHeights={getBatches(tank.id) || []}    
                      inventoryValue={tank.inventory_value || 0}
                      tankData={tank}
                      compacctnum={compacctnum}
                      onAdded={getFullData}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center min-w-[220px] gap-4">
                  <b style={{ fontSize: '3.2rem', fontStyle: 'italic', color: '#e58741' }}>{t('NO_TANKS_AVAILABLE')}</b>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenCreateTank(true)}
                  >
                    {t('ADD_TANK')}
                  </Button>
                </div>
              )}
          </div>
          {/* Recent Activity */}
          <div className='flex flex-1'>
          </div>
        </div>
      </div>
    </div>
  );

  function getBatches(tankId) {
    let batches = fuelBatches.filter(batch => batch.container_tank_id === tankId);
    return batches.sort((a, b) => a.batch_id.split('-')[2] - b.batch_id.split('-')[2]);
  }

  function getFuelInventoryValue(batches) {
    return batches.reduce((total, batch) => total + ( Math.fround(batch.fuel_qty || 0)) *  Math.fround(batch.price_per_gallon || 0), 0); // Assuming $4.2 per gallon
  }
};

export default FuelInventory;
